import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { unifiedApiService } from '../../services/unified';
import { queryKeys } from '../../lib/queryClient';
import type {
  JobTemplate,
  CreateJobTemplateRequest,
  UpdateJobTemplateRequest,
  JobTemplateListResponse,
  JobTemplateGroupedResponse,
  MessageResponse,
} from '../../services/unified/types';

/**
 * Query hook for getting all job templates (system + custom)
 */
export const useJobTemplates = () => {
  return useQuery({
    queryKey: [...queryKeys.admin.all, 'jobTemplates'],
    queryFn: (): Promise<JobTemplateListResponse> => {
      return unifiedApiService.jobTemplates.getTemplates();
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - templates don't change often
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Query hook for getting system templates only
 */
export const useSystemTemplates = () => {
  return useQuery({
    queryKey: [...queryKeys.admin.all, 'jobTemplates', 'system'],
    queryFn: (): Promise<JobTemplateListResponse> => {
      return unifiedApiService.jobTemplates.getSystemTemplates();
    },
    staleTime: 30 * 60 * 1000, // 30 minutes - system templates rarely change
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Query hook for getting custom templates only
 */
export const useCustomTemplates = () => {
  return useQuery({
    queryKey: [...queryKeys.admin.all, 'jobTemplates', 'custom'],
    queryFn: (): Promise<JobTemplateListResponse> => {
      return unifiedApiService.jobTemplates.getCustomTemplates();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Query hook for getting a single job template by ID
 */
export const useJobTemplate = (id: string) => {
  return useQuery({
    queryKey: [...queryKeys.admin.all, 'jobTemplates', id],
    queryFn: (): Promise<JobTemplate> => {
      return unifiedApiService.jobTemplates.getTemplateById(id);
    },
    enabled: !!id,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Mutation hook for creating a custom job template
 */
export const useCreateTemplate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateJobTemplateRequest): Promise<JobTemplate> => {
      return unifiedApiService.jobTemplates.createTemplate(data);
    },
    onSuccess: () => {
      // Invalidate all template queries to show new template
      queryClient.invalidateQueries({ queryKey: [...queryKeys.admin.all, 'jobTemplates'] });
    },
  });
};

/**
 * Mutation hook for updating a custom job template with optimistic updates
 */
export const useUpdateTemplate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateJobTemplateRequest }): Promise<JobTemplate> => {
      return unifiedApiService.jobTemplates.updateTemplate(id, data);
    },
    // Optimistic update
    onMutate: async ({ id, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [...queryKeys.admin.all, 'jobTemplates', id] });
      
      // Snapshot the previous value
      const previousTemplate = queryClient.getQueryData<JobTemplate>([...queryKeys.admin.all, 'jobTemplates', id]);
      
      // Optimistically update to the new value
      if (previousTemplate) {
        queryClient.setQueryData<JobTemplate>([...queryKeys.admin.all, 'jobTemplates', id], {
          ...previousTemplate,
          ...data,
          job_data: data.job_data ? { ...previousTemplate.job_data, ...data.job_data } : previousTemplate.job_data,
        });
      }
      
      // Return context with the snapshot
      return { previousTemplate };
    },
    onSuccess: (updatedTemplate, variables) => {
      // Update the specific template in cache with server response
      queryClient.setQueryData([...queryKeys.admin.all, 'jobTemplates', variables.id], updatedTemplate);
      
      // Invalidate template lists to reflect changes
      queryClient.invalidateQueries({ queryKey: [...queryKeys.admin.all, 'jobTemplates'] });
    },
    // Rollback on error
    onError: (_err, { id }, context) => {
      if (context?.previousTemplate) {
        queryClient.setQueryData([...queryKeys.admin.all, 'jobTemplates', id], context.previousTemplate);
      }
    },
  });
};

/**
 * Mutation hook for deleting a custom job template
 */
export const useDeleteTemplate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string): Promise<MessageResponse> => {
      return unifiedApiService.jobTemplates.deleteTemplate(id);
    },
    onSuccess: (_, id) => {
      // Remove the template from cache
      queryClient.removeQueries({ queryKey: [...queryKeys.admin.all, 'jobTemplates', id] });
      
      // Invalidate template lists to reflect deletion
      queryClient.invalidateQueries({ queryKey: [...queryKeys.admin.all, 'jobTemplates'] });
    },
  });
};


// ============================================================================
// Company-Specific Template Hooks
// ============================================================================

/**
 * Query hook for getting templates for a specific company
 * Returns grouped response with systemTemplates and companyTemplates
 */
export const useCompanyTemplates = (companyId: string) => {
  return useQuery({
    queryKey: [...queryKeys.admin.all, 'companies', companyId, 'templates'],
    queryFn: (): Promise<JobTemplateGroupedResponse> => {
      return unifiedApiService.jobTemplates.getCompanyTemplates(companyId);
    },
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Query hook for getting available templates (system + company-specific)
 * Used in job creation to show relevant templates
 */
export const useAvailableTemplates = (companyId?: string) => {
  return useQuery({
    queryKey: [...queryKeys.admin.all, 'jobTemplates', 'available', companyId || 'all'],
    queryFn: (): Promise<JobTemplateListResponse> => {
      return unifiedApiService.jobTemplates.getAvailableTemplates(companyId);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Query hook for getting templates grouped by type (for job creation UI)
 */
export const useTemplatesForJobCreation = (companyId?: string) => {
  return useQuery({
    queryKey: [...queryKeys.admin.all, 'jobTemplates', 'forCreation', companyId || 'all'],
    queryFn: () => {
      return unifiedApiService.jobTemplates.getTemplatesForJobCreation(companyId);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Mutation hook for creating a company template
 */
export const useCreateCompanyTemplate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ companyId, data }: { 
      companyId: string; 
      data: CreateJobTemplateRequest 
    }): Promise<JobTemplate> => {
      return unifiedApiService.jobTemplates.createCompanyTemplate(companyId, data);
    },
    onSuccess: (_, { companyId }) => {
      // Invalidate company templates
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'companies', companyId, 'templates'] 
      });
      // Invalidate available templates
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'jobTemplates', 'available'] 
      });
      // Invalidate all templates
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'jobTemplates'] 
      });
    },
  });
};

/**
 * Mutation hook for updating a company template
 */
export const useUpdateCompanyTemplate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ 
      companyId, 
      templateId, 
      data 
    }: { 
      companyId: string; 
      templateId: string; 
      data: UpdateJobTemplateRequest 
    }): Promise<JobTemplate> => {
      return unifiedApiService.jobTemplates.updateCompanyTemplate(companyId, templateId, data);
    },
    onSuccess: (_, { companyId, templateId }) => {
      // Invalidate specific template
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'jobTemplates', templateId] 
      });
      // Invalidate company templates
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'companies', companyId, 'templates'] 
      });
      // Invalidate available templates
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'jobTemplates', 'available'] 
      });
    },
  });
};

/**
 * Mutation hook for deleting a company template
 */
export const useDeleteCompanyTemplate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ 
      companyId, 
      templateId 
    }: { 
      companyId: string; 
      templateId: string 
    }): Promise<MessageResponse> => {
      return unifiedApiService.jobTemplates.deleteCompanyTemplate(companyId, templateId);
    },
    onSuccess: (_, { companyId }) => {
      // Invalidate company templates
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'companies', companyId, 'templates'] 
      });
      // Invalidate available templates
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'jobTemplates', 'available'] 
      });
      // Invalidate all templates
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'jobTemplates'] 
      });
    },
  });
};

// ============================================================================
// Job Composer Template Hooks (Requirements 3.1, 6.1)
// ============================================================================

/**
 * Query hook for getting templates for Job Composer (company-only, excludes system templates)
 * Returns templates grouped by type (regular and AI)
 * 
 * Requirements:
 * - 3.1: Filter templates to show only company's templates
 * - 6.1: Exclude system-level templates from the dropdown
 */
export const useJobComposerTemplates = (companyId: string | undefined) => {
  return useQuery({
    queryKey: [...queryKeys.admin.all, 'jobTemplates', 'composer', companyId || 'none'],
    queryFn: async (): Promise<{
      regularTemplates: JobTemplate[];
      aiTemplates: JobTemplate[];
    }> => {
      if (!companyId) {
        return { regularTemplates: [], aiTemplates: [] };
      }
      return unifiedApiService.jobTemplates.getJobComposerTemplates(companyId);
    },
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: (previousData) => previousData,
  });
};
